import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { BaseService } from '../../services/base.service';
import { Order, IOrderedByOption, ITransferLegAssignment } from '../../models/customer-orders/orders';
import { ITransferOrderDetail } from '../../models/customer-orders/transfer-order-detail.model';
import { Driver } from '../../models/customer-orders/driver';
import { AvailableDriverResponse, hasDriver } from '../../models/customer-orders/available-driver';
import { INote, Notes, OrderReminderNote, PatientComment } from '../../models/patients/note';
import { OrderHistoryItem, IOrderHistoryResponse } from '../../models/customer-orders/order-history.model';
import { appendCriteria } from '../../shared/utility';

@Injectable()
export class OrderService extends BaseService<Order> {
	private _transferOrderLoadError$ = new Subject<string>();

	/**
	 * DOVE-2387 — emits when a dispatch transfer-order feed fails. The board keeps rendering
	 * the regular orders (a missing Dispatcher role must not blank the page), but the failure
	 * used to be swallowed and was indistinguishable from "there are no transfer orders".
	 */
	readonly transferOrderLoadError$: Observable<string> = this._transferOrderLoadError$.asObservable();

	constructor(http: HttpClient) {
		super(http);
	}

	/**
	 * DOVE-2112 — merged active company users + active client users with access to
	 * the given insurance. insurance_id omitted → company users only. Loaded once per
	 * form open / insurance change; filtering is client-side.
	 */
	getOrderedByOptions(insuranceId?: number | string | null): Observable<IOrderedByOption[]> {
		let params = new HttpParams();
		if (insuranceId !== null && insuranceId !== undefined && insuranceId !== '') {
			params = params.set('insurance_id', String(insuranceId));
		}

		return this.http.get<any[]>(`${this.baseUrl}orders/ordered-by-options`, { params }).pipe(
			map((options) =>
				(options || []).map((option) => ({
					value: option.value ?? option.id ?? option.user_id,
					label: option.label ?? `${option.firstname ?? ''} ${option.lastname ?? ''}`.trim(),
					email: option.email ?? '',
					group: (option.group === 'Client' ? 'Client' : 'Company') as 'Company' | 'Client'
				}))
			)
		);
	}

	undoTestAssignment(): Observable<any> {
		return this.http.get<any>(`${this.baseUrl}undo-dispatcher-test`).pipe(
			map((response: any) => {
				return response.data.map((data: any) => new Order(data));
			})
		);
	}

	/**
	 * DOVE-2387 — `branch_id` must be OMITTED when no branch is picked, never sent as
	 * `String(null)`. The literal string "null" passes the backend's `filled()` check and
	 * then fails the branch `in_array`, so branch filtering silently stopped applying.
	 */
	private buildDispatchParams(
		branchId: number | null | undefined,
		startDate: string,
		endDate: string
	): Record<string, string> {
		const params: Record<string, string> = { start_date: startDate, end_date: endDate };
		if (branchId !== null && branchId !== undefined && String(branchId) !== '') {
			params['branch_id'] = String(branchId);
		}
		return params;
	}

	/**
	 * DOVE-2387 — a transfer feed request. `legs=1` expands a branch-to-branch transfer into
	 * one row per assignable leg; without the flag the response is the legacy per-order shape.
	 */
	private fetchTransferOrders(feed: string, params: Record<string, string>, legs: boolean): Observable<Order[]> {
		const requestParams = legs ? { ...params, legs: '1' } : params;

		return this.http.get<any>(`${this.baseUrl}dispatch/transfer-orders/${feed}`, { params: requestParams }).pipe(
			map((response: any) => (response.data || []).map((to: any) => this.mapTransferOrderToOrder(to))),
			catchError((error) => {
				// Keep the board usable — regular orders still render — but say so out loud.
				console.error(`Failed to load ${feed} transfer orders`, error);
				this._transferOrderLoadError$.next(
					error?.status === 403
						? 'Transfer orders were not loaded — viewing them requires the Dispatcher role. The list may be incomplete.'
						: `Transfer orders could not be loaded (HTTP ${error?.status ?? 'error'}). The list may be incomplete.`
				);
				return of([] as Order[]);
			})
		);
	}

	searchUnassignedOrders(branchId: number | null, startDate: string, endDate: string): Observable<Order[]> {
		const params = this.buildDispatchParams(branchId, startDate, endDate);

		const orders$ = this.http
			.get<any>(`${this.baseUrl}dispatch/orders/unassigned`, { params })
			.pipe(map((response: any) => response.data.map((data: any) => new Order(data))));

		const transferOrders$ = this.fetchTransferOrders('unassigned', params, true);

		return forkJoin([orders$, transferOrders$]).pipe(
			map(([orders, transferOrders]) => [...orders, ...transferOrders])
		);
	}

	private mapTransferOrderToOrder(to: any): Order {
		// Map transfer order response to Order format for dispatcher table
		const toBranch = to.to_branch || {};
		const fromBranch = to.from_branch || {};

		// DOVE-2387 — on a leg row every assignment-relevant field is the LEG's, not the order's:
		// a Handoff's two legs sit at different branches, with their own tracker status and their
		// own driver. Fall back to the order-level fields for the un-expanded (legacy) shape.
		const leg = to.leg || undefined;
		const legAssignment = leg ? to.leg_driver_truck_assignment : to.driver_truck_assignment;
		const trackerStatus = leg ? to.leg_dispatch_tracker_status : to.dispatch_tracker_status;
		const trackerStatusId = leg ? to.leg_dispatch_tracker_status_id : to.dispatch_tracker_status_id;

		// Parse city and state from to_branch address (format: "address, CITY, STATE ZIPCODE")
		const addressParts = (toBranch.address || '').split(',');
		const cityState = addressParts.length >= 3 ? addressParts[addressParts.length - 2]?.trim() : '';
		const stateZip = addressParts.length >= 3 ? addressParts[addressParts.length - 1]?.trim() : '';
		const state = stateZip.split(' ')[0] || '';

		return new Order({
			order_id: to.id,
			patient_id: null,
			// No patient for transfer orders
			patient_name: '-',
			// Show branch-to-branch info in Location column
			patient_location: `${fromBranch.branch_name || ''} → ${toBranch.branch_name || ''}`,
			scheduled_at: to.scheduled_date,
			order_type: { type: to.transfer_type_display || to.transfer_type },
			// DOVE-2330 — `dispatch_tracker_status` is the display string; the id ships
			// separately on TransferOrderResource. Pass it through or the board's lock gates
			// see `dtsId === null` and treat every transfer row as unlocked.
			dispatch_tracker_status: { name: trackerStatus },
			dispatch_tracker_status_id: trackerStatusId ?? null,
			// TransferOrderResource nests the driver under `driver: { name }`, but the Order
			// model expects `related_user.fullname` (the shape regular orders use). Without
			// this remap, Order's constructor throws on `related_user.fullname` for any
			// transfer order that actually has a driver assigned (Completed/Assigned tabs).
			driver_truck_assignment: legAssignment
				? {
						id: legAssignment.id,
						related_user: { fullname: legAssignment.driver?.name || '' }
					}
				: null,
			// Address info for map markers
			address: {
				address_1: toBranch.address || '',
				address_2: '',
				city: cityState,
				state: state,
				zipcode: stateZip.split(' ')[1] || ''
			},
			current_full_address: toBranch.address || '',
			current_city: cityState,
			current_state: state,
			// Flag to identify as transfer order
			isTransferOrder: true,
			leg,
			// A Handoff yields two rows sharing `to.id`; `leg_key` is what keeps them apart.
			leg_key: to.leg_key || String(to.id),
			leg_branch_id: to.leg_branch_id ?? null,
			leg_branch_name: to.leg_branch_name || ''
		});
	}

	getOrdersWithWrap(): Observable<Order[]> {
		const endPoint = 'order-with-addressesxxxxxx';
		return this.getAll(endPoint).pipe(
			map((response: any) => {
				return response.data.map((data: any) => new Order(data));
			})
		);
	}

	getOrders(): Observable<Order[]> {
		const endPoint = 'open-orders-for-assignment';
		return this.getAll(endPoint).pipe(
			map((response: any) => {
				return response.data.map((data: any) => new Order(data));
			})
		);
	}

	updateOrder(recordObject: Order): Observable<Order> {
		const orderFromForm = new Order(recordObject);
		const originalFormat = orderFromForm.toOriginalFormat();
		const endPoint = 'order-editxxxxxx';
		return this.update<any>(`${endPoint}`, originalFormat['order_id'], originalFormat);
	}

	addOrder(recordObject: Order): Observable<Order> {
		const orderFromForm = new Order(recordObject);
		const originalFormat = orderFromForm.toOriginalFormat();
		const endPoint = 'order-addxxxxx';
		return this.create<any>(`${endPoint}`, originalFormat);
	}

	assignDriverToOrders(driverAndOrders: any): Observable<any> {
		// const orderFromForm = new Order(recordObject);
		// const originalFormat = orderFromForm.toOriginalFormat();
		const endPoint = 'assign-order-driver-multiple';
		return this.updateMultipleRecords<any>(`${endPoint}`, driverAndOrders);
	}

	bulkAssignOrders(orderIds: number[], driverTruckAssignmentId: number): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}dispatch/orders/assignments/bulk`, {
			order_ids: orderIds,
			driver_truck_assignment_id: driverTruckAssignmentId
		});
	}

	bulkReassignOrders(orderIds: number[], driverTruckAssignmentId: number): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}dispatch/orders/reassignments/bulk`, {
			order_ids: orderIds,
			driver_truck_assignment_id: driverTruckAssignmentId
		});
	}

	/**
	 * DOVE-2387 — the transfer bulk endpoints are leg-aware: each entry names the leg being
	 * assigned, because a Handoff's sending and receiving legs take drivers from different
	 * branches. The legacy `{transfer_order_ids}` shape is deprecated; always send `assignments`.
	 */
	bulkAssignTransferOrders(assignments: ITransferLegAssignment[], driverTruckAssignmentId: number): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}dispatch/transfer-orders/assignments/bulk`, {
			assignments,
			driver_truck_assignment_id: driverTruckAssignmentId
		});
	}

	bulkReassignTransferOrders(
		assignments: ITransferLegAssignment[],
		driverTruckAssignmentId: number
	): Observable<any> {
		return this.http.post<any>(`${this.baseUrl}dispatch/transfer-orders/reassignments/bulk`, {
			assignments,
			driver_truck_assignment_id: driverTruckAssignmentId
		});
	}

	unAssignTransferOrders(assignments: ITransferLegAssignment[]): Observable<any> {
		return this.http.delete<any>(`${this.baseUrl}dispatch/transfer-orders/assignments/bulk`, {
			body: { assignments }
		});
	}

	/**
	 * DOVE-2387 — the dispatcher detail payload for a transfer. `orders/{id}` 404s for these,
	 * and this response already carries the items, the legs and the PST notes, so the dialog
	 * needs no companion requests.
	 */
	getTransferOrderById(transferOrderId: number): Observable<ITransferOrderDetail> {
		return this.http.get<ITransferOrderDetail>(`${this.baseUrl}dispatch/transfer-orders/${transferOrderId}`);
	}

	/** Transfers have no notes-only GET; re-fetch the detail after posting to refresh the list. */
	addTransferOrderNote(transferOrderId: number, note: INote) {
		return this.http.post(`${this.baseUrl}dispatch/transfer-orders/${transferOrderId}/notes`, note);
	}

	unAssignOrders(orderIds: number[]): Observable<any> {
		// Validated bulk-unassign endpoint (blocks DTS states like In Progress server-side),
		// not the legacy `unassign-multiple-orders` route which has no DTS check.
		return this.http.delete<any>(`${this.baseUrl}dispatch/orders/assignments/bulk`, {
			body: { order_ids: orderIds }
		});
	}

	getDriversList(branchId: number[], serviceDate: string): Observable<Driver[]> {
		let params = new HttpParams();
		branchId.forEach((branchId) => {
			params = params.append('branch[]', branchId.toString());
		});
		params = params.append('serviceDate', serviceDate);

		// DOVE-2517 — opt-in flag; only the dispatcher board hides placeholder vehicle assignments
		// (e.g. "SWO PENDING (HO)"). Every other caller of get-available-driver still sees them.
		params = params.append('exclude_placeholders', '1');

		// DOVE-2298 — a roster row can carry a null driver (orphaned assignment); dropping it here
		// keeps a single bad row from failing the whole dispatcher forkJoin.
		return this.http
			.get<AvailableDriverResponse[]>(`${this.baseUrl}get-available-driver`, { params })
			.pipe(map((response) => response.filter(hasDriver).map((data) => new Driver(data))));
	}

	submitRoute(recordObject: any): Observable<any> {
		const orderFromForm = new recordObject();
		const originalFormat = orderFromForm.toOriginalFormat();
		const endPoint = 'order-addxxxxx';
		return this.create<any>(`${endPoint}`, originalFormat);
	}

	// getDriversList2(branchIds: number[], startDate: string, endDate: string): Observable<User[]> {
	//       // const params = { branchIds, startDate, endDate };

	//       let httpParam = new HttpParams();
	//       //open-orders-for-assignment

	//       branchIds.forEach(branchId => {
	//         httpParam = httpParam.append('branchId', branchId.toString());
	//       });
	//       httpParam = httpParam.append('startDate', startDate);
	//       httpParam = httpParam.append('endDate', endDate);

	//       return this.http.get<User[]>(`${this.baseUrl}get-drivers-list-on-duty`, { httpParam }).pipe(
	//         map((response: any) => {
	//           return response.data.map((data: any) => new Order(data));
	//         })
	//       );
	//     }

	getOrdersForTodayByDriver(): Observable<any> {
		return this.http.get('/api/driver/orders/today');
	}

	getOrderNotes(orderId: number) {
		return this.http.get<any[]>(`${this.baseUrl}orders/${orderId}/notes`).pipe(
			map((res) => {
				return res.map((note) => new Notes(note));
			})
		);
	}

	// PST notes for the dispatcher order modal, returned regardless of viewed status.
	getDispatcherOrderNotes(orderId: number) {
		return this.http.get<any[]>(`${this.baseUrl}orders/${orderId}/dispatcher-notes`).pipe(
			map((res) => {
				return res.map((note) => new Notes(note));
			})
		);
	}

	getOrderPatientNotes(patientId: number) {
		return this.http.get<any[]>(`${this.baseUrl}patient/${patientId}/notes`).pipe(
			map((res) => {
				return res.map((note) => new Notes(note));
			})
		);
	}

	getOrderPopupNotes(orderId: number) {
		return this.http.get<any>(`${this.baseUrl}order/${orderId}/popup-notes`).pipe(
			map((res) => {
				return res.has_popup ? res.notes.map((note: any) => new Notes(note)) : [];
			})
		);
	}

	getOrderReminderNotes(orderId: number): Observable<OrderReminderNote[]> {
		return this.http
			.get<any[]>(`${this.baseUrl}orders/${orderId}/order-reminder-notes`)
			.pipe(map((res) => (res || []).map((note) => new OrderReminderNote(note))));
	}

	addOrderNote(orderId: number, note: INote) {
		return this.http.post(`${this.baseUrl}orders/${orderId}/notes`, note);
	}

	getOrderNoteComments(noteId: number): Observable<PatientComment[]> {
		return this.http.get<any[]>(`${this.baseUrl}note/${noteId}/comments`).pipe(
			map((res) => {
				return res.map((cmt) => new PatientComment(cmt));
			})
		);
	}

	addOrderNoteComment(noteId: number, comment: any) {
		return this.http.post(`${this.baseUrl}note/${noteId}/comments`, comment);
	}

	getOrderNoteById(orderId: number, noteId: number): Observable<Notes> {
		return this.http.get<any>(`${this.baseUrl}orders/${orderId}/notes/${noteId}`).pipe(
			map((res) => {
				return new Notes(res);
			})
		);
	}

	updateOrderNote(orderId: number, noteId: number, note: INote) {
		return this.http.put(`${this.baseUrl}orders/${orderId}/notes/${noteId}`, note);
	}

	getOrderNoteCategories(): Observable<{ id: number; name: string }[]> {
		return this.http.get<any[]>(`${this.baseUrl}note-categories`);
	}

	cancelOrder(orderId: number, reason: string): Observable<any> {
		return this.http.put(`${this.baseUrl}orders/${orderId}/cancel-order`, { reason });
	}

	confirmOrder(orderId: number): Observable<any> {
		return this.http.put(`${this.baseUrl}orders/${orderId}/confirm-order`, {});
	}

	dispatchOrder(orderId: number): Observable<any> {
		return this.http.put(`${this.baseUrl}orders/${orderId}/dispatch-order`, {});
	}

	reopenOrder(orderId: number): Observable<any> {
		return this.http.put(`${this.baseUrl}orders/${orderId}/reopen-order`, {});
	}

	voidOrder(orderId: number, force = false, reason = ''): Observable<any> {
		const body = force ? { reason, force: true } : { reason };
		return this.http.put(`${this.baseUrl}orders/${orderId}/void-order`, body);
	}

	printBlankOrder(orderId: number): Observable<Blob> {
		return this.http.get(`${this.baseUrl}orders/${orderId}/print-blank-order`, { responseType: 'blob' });
	}

	searchAssignedOrders(branchId: number | null, startDate: string, endDate: string): Observable<Order[]> {
		const params = this.buildDispatchParams(branchId, startDate, endDate);

		const orders$ = this.http
			.get<any>(`${this.baseUrl}dispatch/orders/assigned`, { params })
			.pipe(map((response: any) => response.data.map((data: any) => new Order(data))));

		// DOVE-2387 — this feed never merged transfer orders, so a transfer leg simply vanished
		// from the board the moment it was assigned successfully.
		const transferOrders$ = this.fetchTransferOrders('assigned', params, true);

		return forkJoin([orders$, transferOrders$]).pipe(
			map(([orders, transferOrders]) => [...orders, ...transferOrders])
		);
	}

	searchCompletedOrders(branchId: number | null, startDate: string, endDate: string): Observable<Order[]> {
		const params = this.buildDispatchParams(branchId, startDate, endDate);

		const orders$ = this.http
			.get<any>(`${this.baseUrl}dispatch/orders/completed`, { params })
			.pipe(map((response: any) => response.data.map((data: any) => new Order(data))));

		// Completed rows are read-only, so they stay in the legacy per-order shape.
		const transferOrders$ = this.fetchTransferOrders('completed', params, false);

		return forkJoin([orders$, transferOrders$]).pipe(
			map(([orders, transferOrders]) => [...orders, ...transferOrders])
		);
	}

	getOrderHistory(
		orderId: number,
		page: number = 1,
		perPage: number = 50,
		filters: {
			date?: string;
			userName?: string;
			search?: string;
			sortBy?: 'created_at' | 'user_name' | 'description';
			sortDirection?: 'asc' | 'desc';
		} = {}
	): Observable<IOrderHistoryResponse> {
		let params = new HttpParams().set('page', page).set('per_page', perPage);

		if (filters.date) params = params.set('date', filters.date);
		if (filters.userName) params = params.set('user_name', filters.userName);
		if (filters.search) params = params.set('search', filters.search);
		if (filters.sortBy) params = params.set('sort_by', filters.sortBy);
		if (filters.sortDirection) params = params.set('sort_direction', filters.sortDirection);

		return this.http.get<any>(`${this.baseUrl}order/${orderId}/history`, { params }).pipe(
			map((response: any) => {
				return {
					data: response.data.map((item: any) => new OrderHistoryItem(item)),
					links: response.links,
					meta: {
						currentPage: response.meta.current_page,
						from: response.meta.from,
						lastPage: response.meta.last_page,
						links: response.meta.links,
						path: response.meta.path,
						perPage: response.meta.per_page,
						to: response.meta.to,
						total: response.meta.total
					}
				} as IOrderHistoryResponse;
			})
		);
	}
}
